import React, { useEffect, useRef } from 'react';
import { Box, Typography, IconButton, Tooltip, alpha } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { CartItemRow, CartRowItem } from './CartItemRow';
import { PDVThemeColors } from './PDVThemes';
import { pdvScale, MQ_PHONE } from './pdvScale';

interface CartItemsListProps {
  items: CartRowItem[];
  colors: PDVThemeColors;
  formatQuantityBR: (valor: number) => string;
  formatMoneyBR: (valor: number) => string;
  onRemove: (iditens: string) => void;
  // Botão "Nova" no header da listagem, na frente de "Total" — abre a
  // dialog de confirmação (ver Pdv.tsx). Opcional só pra não quebrar quem
  // ainda não passa essa prop.
  onNovaVenda?: () => void;
}

// "Relação de itens" — lista contínua estilo terminal de PDV, sem card por
// item. Só apresentação: recebe o mesmo array `rows` que o Pdv.tsx já
// mantinha e as mesmas funções de formatação/remoção, sem duplicar lógica.
// React.memo — mesmo motivo do CartItemRow (ver lá): evita repintar a
// listagem inteira a cada tick do relógio do header.
export const CartItemsList: React.FC<CartItemsListProps> = React.memo(({
  items, colors, formatQuantityBR, formatMoneyBR, onRemove, onNovaVenda,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastCount = useRef(items.length);

  // Acompanha o último item ao adicionar um novo — mas só quando a lista
  // cresce, pra não brigar com o operador rolando manualmente.
  useEffect(() => {
    if (items.length > lastCount.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    lastCount.current = items.length;
  }, [items.length]);

  return (
    // Moldura TRANSPARENTE — quem rola (ref=scrollRef) e quem define a
    // altura disponível (flex:1 + minHeight:0 dentro do pai com flex
    // column, ver Pdv.tsx). flex:1+minHeight:0 direto neste Box (que também
    // tem overflowY:'auto') não depende de porcentagem nenhuma: o navegador
    // sempre calcula uma altura final (finita) pra um item flex com flex:1
    // dentro de um pai com altura definida, e overflow-y:auto rola qualquer
    // conteúdo que passe disso — é o padrão mais robusto pra "lista rolável
    // com rodapé fixo", sem empurrar o SubTotal.
    // SEM justifyContent:'flex-end' aqui — é exatamente esse o motivo do
    // scroll não funcionar com muitos itens (bug confirmado medindo o DOM
    // de verdade: um flex container com overflow-y:auto + justify-content
    // diferente de flex-start faz o Chrome/WebKit calcular scrollHeight
    // igual a clientHeight mesmo com conteúdo maior que a área visível —
    // ou seja, o navegador "acha" que não tem nada pra rolar, mesmo tendo).
    // O "grudar embaixo com poucos itens" agora vem de marginTop:'auto' no
    // Box de dentro (ver abaixo) em vez de justify-content no de fora: com
    // poucos itens, a margem auto empurra ele pro final normalmente; assim
    // que o conteúdo estoura a área disponível, essa margem simplesmente
    // vira 0 (não sobra espaço livre pra distribuir) e o scroll passa a
    // funcionar do jeito padrão, sem a ambiguidade que quebrava o cálculo.
    <Box
      ref={scrollRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        width: { xs: '100%', sm: '50%' },
        '&::-webkit-scrollbar': { width: pdvScale(6) },
        '&::-webkit-scrollbar-thumb': { backgroundColor: alpha('#000000', 0.15), borderRadius: pdvScale(3) },
      }}
    >
      <Box
        sx={{
          // Fundo fixo branco (não mais colors.cartItemBackground do tema) —
          // a listagem agora usa uma paleta neutra própria (zebrado
          // off-white/branco, ver CartItemRow), independente do tema de cor
          // escolhido no rodapé, a pedido. Sem flex:1 — a altura é a do
          // próprio conteúdo (cabeçalho + itens), nunca mais que isso.
          bgcolor: '#FFFFFF',
          borderTopLeftRadius: pdvScale(16),
          borderTopRightRadius: pdvScale(16),
          // Substitui o justifyContent:'flex-end' que estava no Box de fora
          // (ver comentário acima) — mesmo efeito visual (gruda embaixo
          // quando sobra espaço), mas sem quebrar o cálculo de scrollHeight
          // quando o conteúdo estoura.
          marginTop: 'auto',
          // SEM overflow:hidden aqui — position:sticky no cabeçalho logo
          // abaixo depende do Box que rola de verdade (o de fora,
          // overflowY:'auto') ser o único ancestral com overflow != visible;
          // um overflow:hidden NESTE Box (entre o sticky e o scroller)
          // quebraria o "grudar no topo" ao rolar. O raio arredondado do
          // cabeçalho (mesmo valor, ver abaixo) já resolve visualmente sem
          // precisar clipar nada aqui.
        }}
      >
        {/* position:sticky — fica fixo no topo da listagem enquanto rola,
            em vez de sumir de vista com muitos itens (comportamento normal
            de cabeçalho de tabela). Fundo escuro na cor do tema (a pedido,
            ver imagem de referência) — antes era branco/cinza claro; agora
            usa primaryDark + linhas verticais discretas separando cada
            coluna, texto branco em caixa alta bem legível. */}
        <Box
          sx={{
            // Some no celular — as linhas de item quebram em 2 linhas ali
            // (ver CartItemRow), então esse cabeçalho de coluna única linha
            // não bate mais com as colunas de baixo; cada linha do item já
            // é auto-explicativa sem precisar do título em cima.
            display: 'flex',
            px: pdvScale(12),
            py: pdvScale(12),
            fontSize: pdvScale(19),
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: colors.textOnPrimary,
            bgcolor: colors.primaryDark,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            // Mesmo raio do Box de fora (ver acima) — como aquele Box não
            // pode ter overflow:hidden (quebraria o sticky), o cabeçalho
            // (primeiro filho, mesma posição do canto) já nasce arredondado
            // pra combinar, sem precisar clipar nada.
            borderTopLeftRadius: pdvScale(16),
            borderTopRightRadius: pdvScale(16),
            [MQ_PHONE]: {
              display: 'none',
            },
          }}
        >
          {/* Linha vertical entre cada título (menos o último) — mesmo tom
              do texto do cabeçalho, bem discreta. minWidth em cada um bate
              com o minWidth das colunas de dados (ver CartItemRow.tsx) —
              sem isso o título quebrava em 2 linhas ("VL. UNIT") numa
              largura que os dados embaixo já tinham parado de encolher,
              destoando um do outro. */}
          <Box sx={{ flex: '0 0 6%', minWidth: pdvScale(34), textAlign: 'center', borderRight: `1px solid ${alpha(colors.textOnPrimary, 0.2)}` }}>Nº</Box>
          <Box sx={{ flex: '1 1 49%', minWidth: 0, pl: pdvScale(12), borderRight: `1px solid ${alpha(colors.textOnPrimary, 0.2)}` }}>Descrição do Produto</Box>
          <Box sx={{ flex: '0 0 9%', minWidth: pdvScale(40), textAlign: 'center', borderRight: `1px solid ${alpha(colors.textOnPrimary, 0.2)}` }}>Qtd</Box>
          <Box sx={{ flex: '0 0 8%', minWidth: pdvScale(36), textAlign: 'center', borderRight: `1px solid ${alpha(colors.textOnPrimary, 0.2)}` }}>Und</Box>
          <Box sx={{ flex: '0 0 13%', minWidth: pdvScale(78), textAlign: 'right', pr: pdvScale(12), borderRight: `1px solid ${alpha(colors.textOnPrimary, 0.2)}`, whiteSpace: 'nowrap' }}>Vl. Unit</Box>
          {/* Total + botão "Nova" — mesma coluna/linha da lixeira nas linhas
              de item abaixo (ver CartItemRow.tsx: Total também reserva
              espaço extra pro ícone ali). "Nova" só aparece se a prop for
              passada (onNovaVenda). */}
          <Box sx={{ flex: '0 0 15%', minWidth: pdvScale(100), display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: pdvScale(6) }}>
            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>Total</Box>
            {onNovaVenda && (
              <Tooltip title="Nova venda">
                <IconButton
                  size="small"
                  onClick={onNovaVenda}
                  sx={{
                    color: colors.textOnPrimary,
                    p: pdvScale(4),
                    '&:hover': { backgroundColor: alpha(colors.textOnPrimary, 0.15) },
                  }}
                >
                  <RestartAltIcon sx={{ fontSize: pdvScale(20) }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {items.length > 0 ? (
          items.map((item, index) => (
            <CartItemRow
              key={item.iditens || index}
              index={index}
              item={item}
              colors={colors}
              formatQuantityBR={formatQuantityBR}
              formatMoneyBR={formatMoneyBR}
              onRemove={onRemove}
            />
          ))
        ) : (
          <Box sx={{ height: pdvScale(150), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ color: colors.textSecondary, fontSize: pdvScale(16) }}>
              Nenhum item adicionado.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
});
